import * as React from 'react'

import { action, makeObservable, observable } from 'mobx'
import { inject, observer } from 'mobx-react'

import CalendarPlaceholderStore from '~/client/src/shared/components/CalendarDayView/components/CalendarPlaceholder.store'
import MarkupEditor from '~/client/src/shared/components/MapMarkups/MarkupEditor'
import { IShortcutGroup } from '~/client/src/shared/components/MapMarkups/MarkupShortcutsLegend'
import MovePinControls from '~/client/src/shared/components/MovePinControls/MovePinControls'
import DesktopWorkflowComposer from '~/client/src/shared/components/WorkflowComposer/DesktopWorkflowComposer'
import MobileWorkflowComposer from '~/client/src/shared/components/WorkflowComposer/MobileWorkflowComposer'
import WorkflowComposerStore, {
  IWorkflowComposerDeps,
} from '~/client/src/shared/components/WorkflowComposer/WorkflowComposer.store'
import WorkflowComposerMinimized from '~/client/src/shared/components/WorkflowComposer/WorkflowComposerMinimized'
import WorkflowDrawingGuardDialog from '~/client/src/shared/components/WorkflowDrawingGuard/WorkflowDrawingGuardDialog'
import { WorkflowGlyph } from '~/client/src/shared/components/WorkflowTypeIcon/WorkflowIconGroups'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import WorkflowType from '~/client/src/shared/models/WorkflowType'
import ClosuresStore from '~/client/src/shared/stores/domain/Closures.store'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import { FileUploadingStore } from '~/client/src/shared/stores/domain/FileUploading.store'
import GraphExecutorStore from '~/client/src/shared/stores/domain/GraphExecutor.store'
import LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import MarkupsStore from '~/client/src/shared/stores/domain/Markups.store'
import MaterialCategoriesStore from '~/client/src/shared/stores/domain/MaterialCategories.store'
import MaterialsStore from '~/client/src/shared/stores/domain/Materials.store'
import PermitInspectionChangesStore from '~/client/src/shared/stores/domain/PermitInspectionChanges.store'
import PermitInspectionsStore from '~/client/src/shared/stores/domain/PermitInspections.store'
import PermitStatusChangesStore from '~/client/src/shared/stores/domain/PermitStatusChanges.store'
import ProjectMembersStore from '~/client/src/shared/stores/domain/ProjectMembers.store'
import RecurringWorkflowSettingsStore from '~/client/src/shared/stores/domain/RecurringWorkflowSettings.store'
import SitemapItemsStore from '~/client/src/shared/stores/domain/SitemapItems.store'
import TagsStore from '~/client/src/shared/stores/domain/Tags.store'
import UserProjectsStore from '~/client/src/shared/stores/domain/UserProjects.store'
import WorkflowTypesStore from '~/client/src/shared/stores/domain/WorkflowTypes.store'
import EventsStore from '~/client/src/shared/stores/EventStore/Events.store'
import CommonStore from '~/client/src/shared/stores/ui/Common.store'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'
import WorkflowDrawingGuardStore from '~/client/src/shared/stores/ui/WorkflowDrawingGuard.store'
import { createPinMoveKeyHandler } from '~/client/src/shared/utils/createPinMoveKeyHandler'
import { getFullscreenElement } from '~/client/src/shared/utils/fullscreenElement'
import { isBaseSurfaceActive } from '~/client/src/shared/utils/keyboardClaim'
import { toast } from '~/client/src/shared/utils/toast'

import DomPin from '../SitemapHelpers/components/DomPins/DomPin'
import MapMarkupSessionStore, {
  MapMarkupSessionOutcome,
} from './MapMarkupSession.store'
import {
  isComposerCompletable,
  resolveCreationWorkflowType,
} from './workflowCreationPolicy'

interface IProps {
  session: MapMarkupSessionStore
  // Resolved by the host's creation policy; the host only mounts the overlay when set.
  workflowType: WorkflowType
  // Fired after a successful create so the map can select the new workflow.
  onWorkflowCreated?: (permitId: string) => void
  // Called instead of opening the composer when the workflow type has a mandatory
  // field only the full form can fill. Drawing stays available either way.
  onRouteCreationToForm?: () => void
  // The map's clickable layers drawn above a resting markup.
  yieldToLayerIds?: string[]

  isMobileEnv?: boolean
  common?: CommonStore
  workflowDrawingGuardStore?: WorkflowDrawingGuardStore
  eventsStore?: EventsStore
  projectDateStore?: ProjectDateStore
  fileUploadingStore?: FileUploadingStore
  permitTypesStore?: WorkflowTypesStore
  userProjectsStore?: UserProjectsStore
  projectMembersStore?: ProjectMembersStore
  companiesStore?: CompaniesStore
  locationAttributesStore?: LocationAttributesStore
  materialsStore?: MaterialsStore
  materialCategoryStore?: MaterialCategoriesStore
  tagsStore?: TagsStore
  graphExecutorStore?: GraphExecutorStore
  closuresStore?: ClosuresStore
  markupsStore?: MarkupsStore
  permitStatusChangesStore?: PermitStatusChangesStore
  permitInspectionsStore?: PermitInspectionsStore
  permitInspectionChangesStore?: PermitInspectionChangesStore
  calendarPlaceholderStore?: CalendarPlaceholderStore
  recurringWorkflowSettingsStore?: RecurringWorkflowSettingsStore
  sitemapItemsStore?: SitemapItemsStore
}

// .permit-side-view in the desktop Logistics stylesheet.
const SIDEBAR_FORM_WIDTH_PX = 400

// Chrome of the always-on map-first creation flow (STX1-686): the markup editor
// is live the whole time; a creation spins up the Composer, glued to the pin.
@inject(
  'isMobileEnv',
  'common',
  'workflowDrawingGuardStore',
  'eventsStore',
  'projectDateStore',
  'fileUploadingStore',
  'permitTypesStore',
  'userProjectsStore',
  'projectMembersStore',
  'companiesStore',
  'locationAttributesStore',
  'materialsStore',
  'materialCategoryStore',
  'tagsStore',
  'graphExecutorStore',
  'closuresStore',
  'markupsStore',
  'permitStatusChangesStore',
  'permitInspectionsStore',
  'permitInspectionChangesStore',
  'calendarPlaceholderStore',
  'recurringWorkflowSettingsStore',
  'sitemapItemsStore',
)
@observer
export default class MapMarkupSessionOverlay extends React.Component<IProps> {
  @observable private composer: WorkflowComposerStore = null
  private isRoutingToForm = false
  // Scopes the composer's docked-cluster lookup to this overlay's subtree.
  private readonly rootRef = React.createRef<HTMLDivElement>()

  public constructor(props: IProps) {
    super(props)
    makeObservable(this)
    props.session.onCreationStart = this.handleCreationStart
    props.session.onCreationEnd = this.handleCreationEnd
  }

  public componentDidMount(): void {
    this.props.session.startSession()
    document.addEventListener('keydown', this.handleMovePinKeyDown)
    // Re-adopt an in-flight creation after a mid-submit remount: onCreationStart won't
    // re-fire, so rebuild the composer here or the flow is orphaned with no card.
    if (this.props.session.isCreating) {
      this.handleCreationStart()
    }
  }

  public componentWillUnmount(): void {
    // Submitting is left intact — the in-flight create owns its drafts and
    // completes via handleCreationEnd after unmount.
    document.removeEventListener('keydown', this.handleMovePinKeyDown)
    // Not in dispose(): it early-returns while submitting, which would strand the
    // override on the store.
    this.props.session.mapBoxViewerStore.markupPrefs.clearSessionMarkupsOverride()
    this.props.session.dispose()
    this.composer?.dispose()
    // dispose() skips endCreation while submitting, so the guard's history.block
    // + beforeunload would outlive the overlay — unregister explicitly (idempotent).
    this.props.workflowDrawingGuardStore.unregister()
  }

  // Move-pin mode pans the map under a fixed centre pin, so centring the map on
  // the user's position IS moving the pin there (mobile-only control).
  private moveMapToMyLocation = (): void => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser')
      return
    }
    navigator.geolocation.getCurrentPosition(
      pos =>
        this.props.session.mapBoxViewerStore.mapRef?.flyTo({
          center: [pos.coords.longitude, pos.coords.latitude],
          essential: true,
        }),
      () =>
        toast.error(Localization.translator.somethingWentWrongTryAgainLater),
    )
  }

  // Reposition mode owns Esc (cancel) / Enter (done), mirroring the pill buttons.
  private handleMovePinKeyDown = createPinMoveKeyHandler({
    // Base surface: quiet while an overlay owns the keys.
    isActive: () => this.props.session.isMovingPin && isBaseSurfaceActive(),
    onDone: () => this.props.session.finishMovingPin(),
    onCancel: () => this.props.session.cancelMovingPin(),
  })

  public render() {
    const { session, isMobileEnv, yieldToLayerIds } = this.props
    const Composer = isMobileEnv
      ? MobileWorkflowComposer
      : DesktopWorkflowComposer

    // Mobile shows the toolbar only once creation starts — plain map navigation by
    // default, no accidental tool presses; desktop keeps the editor always-on.
    const isEditorVisible =
      session.isEditorMounted && (!isMobileEnv || session.isCreating)

    return (
      <div
        ref={this.rootRef}
        className="tw:pointer-events-none tw:absolute tw:inset-0"
        // The form overlays the map's right edge instead of shrinking it.
        style={
          session.isHandedOff && !isMobileEnv
            ? ({
                '--markup-toolbar-center': `calc(50% - ${
                  SIDEBAR_FORM_WIDTH_PX / 2
                }px)`,
              } as React.CSSProperties)
            : undefined
        }
      >
        {isEditorVisible && (
          <MarkupEditor
            key={session.editorEpoch}
            editorText={session.editorText}
            mapBoxViewerStore={session.mapBoxViewerStore}
            sitePermitId={session.sessionPermitId}
            projectId={session.projectId}
            // Fullscreen: portal the toolbar pickers into the fullscreen element, not
            // body (which renders behind it). Null off-fullscreen → base-ui body default.
            portalContainer={getFullscreenElement() as HTMLElement | null}
            deferred={true}
            onReady={session.handleEditorReady}
            onDirtyChange={session.handleEditorDirtyChange}
            onClose={session.handleEditorClose}
            onIdleSelectChange={session.handleIdleSelectChange}
            onTextSubjectChange={session.handleTextSubjectChange}
            yieldToLayerIds={yieldToLayerIds}
            // Fold pin moves into the editor's unified undo/redo timeline.
            getLocation={session.getLocationSnapshot}
            onRestoreLocation={session.applyLocationSnapshot}
            // Present whenever the editor is mounted so it can INITIATE a new pin, not
            // only relocate one; mobile mounts the editor only mid-creation → relocate-only.
            showLocationTool={session.isEditorMounted}
            isLocationEditActive={session.isMovingPin}
            onEditPreciseLocation={session.startMovingPin}
            legendExtraGroups={this.workflowLegendGroups}
            // Browse stays free of terra-draw's GL layer stack; a creation
            // (right-click bubble / pin) or a tool pick boots it on demand.
            deferDrawing={true}
            drawingEngaged={session.isCreating || session.isHandedOff}
            // Desktop: while the form is minimized, the toolbar becomes one common-wrapper
            // cluster — a leading drag grip + the tools + the "Open workflow" action —
            // instead of a pin-anchored pill.
            dockedTrailing={
              !isMobileEnv && this.composer?.isMinimized ? (
                <WorkflowComposerMinimized store={this.composer} />
              ) : undefined
            }
            onDockedGripDown={
              !isMobileEnv && this.composer?.isMinimized
                ? this.composer.startDockedDrag
                : undefined
            }
          />
        )}
        {this.composer && session.isCreating && !session.isMovingPin && (
          // A markup tool minimizes the composer (desktop pill, mobile peek) rather
          // than hiding it, so Cancel/Submit stay reachable — no dead-end state.
          <Composer store={this.composer} />
        )}
        {session.isMovingPin && this.renderMovePinMode()}
        <WorkflowDrawingGuardDialog
          store={this.props.workflowDrawingGuardStore}
        />
      </div>
    )
  }

  // Reuses the Precise Location pan-under-a-fixed-pin model.
  private renderMovePinMode(): JSX.Element {
    const { session, isMobileEnv, workflowType } = this.props
    // Not inside the glyph prop: DomPin runs it in its own observer, where props throw.
    const pinIconName = (this.composer?.workflowType ?? workflowType)?.iconName
    return (
      <>
        <div className="tw:pointer-events-none tw:absolute tw:inset-0 tw:z-[5] tw:bg-white/25" />
        <div className="tw:pointer-events-none tw:absolute tw:top-1/2 tw:left-1/2 tw:z-10 tw:-translate-x-1/2 tw:-translate-y-1/2">
          <DomPin
            overlay
            size="lg"
            fill="var(--primary)"
            glyph={({ size, color }) => (
              <WorkflowGlyph iconName={pinIconName} size={size} color={color} />
            )}
          />
        </div>
        <div
          className={
            // Mobile: full-width action bar; desktop: centered pill with keyboard hints.
            isMobileEnv
              ? 'tw:pointer-events-auto tw:absolute tw:inset-x-2 tw:bottom-3 tw:z-20 tw:rounded-lg tw:bg-popover tw:p-2 tw:shadow-[0px_4px_8px_0px_rgba(0,0,0,0.15),0px_1px_3px_0px_rgba(0,0,0,0.3)]'
              : 'tw:pointer-events-auto tw:absolute tw:bottom-3 tw:left-[var(--markup-toolbar-center,50%)] tw:z-20 tw:-translate-x-1/2 tw:rounded-lg tw:bg-popover tw:px-3 tw:py-2 tw:shadow-[0px_4px_8px_0px_rgba(0,0,0,0.15),0px_1px_3px_0px_rgba(0,0,0,0.3)]'
          }
        >
          <MovePinControls
            isMobileEnv={isMobileEnv}
            onCancel={session.cancelMovingPin}
            onDone={session.finishMovingPin}
            onMoveToMyLocation={this.moveMapToMyLocation}
          />
        </div>
      </>
    )
  }

  // Built per render so the strings track the active locale.
  private get workflowLegendGroups(): IShortcutGroup[] {
    return [
      {
        title: Localization.translator.workflow,
        rows: [
          {
            label: Localization.translator.createWorkflow,
            // Keycap label, unlocalized like the legend's other key names.
            keys: ['Right-click'],
          },
        ],
      },
    ]
  }

  @action
  private handleCreationStart = (): void => {
    if (this.composer) {
      return
    }
    const {
      session,
      workflowDrawingGuardStore,
      common,
      workflowType,
      permitTypesStore,
    } = this.props
    const pickedType = resolveCreationWorkflowType(
      permitTypesStore,
      session.creationWorkflowTypeId,
      workflowType,
    )
    // Seeds the entry paths with no pick: markup-first start, first move-pin drop.
    session.setCreationWorkflowType(pickedType?.id)
    // The composer would open with Submit permanently disabled, so hand the creation
    // — pin and drawn markups included — to the full form instead of trapping the user.
    if (!isComposerCompletable(pickedType)) {
      this.routeToForm()
      return
    }
    this.composer = new WorkflowComposerStore(
      session,
      this.deps,
      this.handleComposerClose,
      this.routeToForm,
      () => this.rootRef.current,
    )
    workflowDrawingGuardStore.register(session, common?.history)
  }

  /** handOffCreation never fires onCreationEnd; left alive, this composer swallows the next one. */
  @action
  private routeToForm = (): void => {
    if (this.isRoutingToForm) {
      return
    }
    // Deferred: a creation-start route runs inside the editor's own onDirtyChange.
    this.isRoutingToForm = true
    this.props.workflowDrawingGuardStore.unregister()
    this.composer?.dispose()
    this.composer = null
    Promise.resolve().then(() => {
      this.isRoutingToForm = false
      this.props.onRouteCreationToForm?.()
    })
  }

  @action
  private handleCreationEnd = (outcome: MapMarkupSessionOutcome): void => {
    this.props.workflowDrawingGuardStore.unregister()
    this.composer?.dispose()
    this.composer = null
    if (outcome === 'complete') {
      this.props.onWorkflowCreated?.(this.props.session.lastCreatedPermitId)
    }
  }

  private handleComposerClose = (): void => {
    const { session, workflowDrawingGuardStore } = this.props
    // Confirm only when markups would be lost; a pin-only creation closes
    // immediately (the guard would otherwise nag).
    if (session.hasUnsavedMarkups) {
      workflowDrawingGuardStore.guard(() => session.endCreation('discard'))
    } else {
      session.endCreation('discard')
    }
  }

  private get deps(): IWorkflowComposerDeps {
    const {
      eventsStore,
      projectDateStore,
      fileUploadingStore,
      permitTypesStore,
      userProjectsStore,
      projectMembersStore,
      companiesStore,
      locationAttributesStore,
      materialsStore,
      materialCategoryStore,
      tagsStore,
      graphExecutorStore,
      closuresStore,
      markupsStore,
      permitStatusChangesStore,
      permitInspectionsStore,
      permitInspectionChangesStore,
      calendarPlaceholderStore,
      recurringWorkflowSettingsStore,
      sitemapItemsStore,
    } = this.props
    return {
      eventsStore,
      projectDateStore,
      fileUploadingStore,
      permitTypesStore,
      userProjectsStore,
      projectMembersStore,
      companiesStore,
      locationAttributesStore,
      materialsStore,
      materialCategoryStore,
      tagsStore,
      graphExecutorStore,
      closuresStore,
      markupsStore,
      permitStatusChangesStore,
      permitInspectionsStore,
      permitInspectionChangesStore,
      calendarPlaceholderStore,
      recurringWorkflowSettingsStore,
      sitemapItemsStore,
    }
  }
}
